import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BarChart2, Search, FileText, CreditCard, Network, TrendingUp,
  Check
} from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import { useScrollReveal } from '../hooks/useScrollReveal';
import TestimonialsSection from '../components/TestimonialsSection';
import { HeroSection as NewHeroSection, LogosSection } from '../components/ui/hero-1';

/* ─── Animated Stat ─── */
function AnimatedStat({ numericValue, display, label, source }) {
  const { ref, isVisible } = useScrollReveal(0.5);
  const count = useCountUp(numericValue, 2000, isVisible);
  const isRange = display.includes('-') || display.startsWith('$') || display.endsWith('+');
  const showValue = isRange ? display : display.replace(/\d+/, count);

  return (
    <div
      ref={ref}
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 flex flex-col items-center text-center hover:scale-[1.02] transition-transform duration-300"
      data-testid="stat-card"
    >
      <span className="font-lexend font-bold text-[56px] lg:text-[64px] leading-none text-brand-orange mb-4">
        {showValue}
      </span>
      <p className="font-source-sans text-brand-navy font-semibold text-sm leading-snug mb-4 flex-grow">
        {label}
      </p>
      {source && (
        <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest mt-auto">
          {source}
        </span>
      )}
    </div>
  );
}

/* ─── Reveal wrapper ─── */
function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Stats Bar ─── */
function StatsBar() {
  const stats = [
    { display: '25+', numericValue: 25, label: 'Senior Supply Chain Practitioners' },
    { display: '60-70%', numericValue: 65, label: 'Faster Sourcing Cycle Times' },
    { display: '5-8%', numericValue: 8, label: 'Addressable Spend Savings Delivered' },
    { display: '30%', numericValue: 30, label: 'Shorter P2P Cycle Achieved' },
  ];

  return (
    <section className="bg-brand-muted py-12" data-testid="stats-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <AnimatedStat key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Industry Ticker ─── */
function IndustryTicker() {
  const industries = [
    'Strategic Sourcing & Procurement',
    'Sourcing',
    'Procure-to-Pay (P2P)',
    'Contract Lifecycle Management (CLM)',
    'Supplier Lifecycle & Risk Management',
    'Invoice Automation',
    'Spend Analytics',
    'Business Networks',
    'Document Intelligence / AI Automation',
    'ERP & Finance Integrations',
    'Cloud & Workflow Platforms',
  ];

  const allItems = [...industries, ...industries];

  return (
    <section className="py-4 bg-brand-navy overflow-hidden" data-testid="industry-ticker">
      <div className="marquee-track">
        {allItems.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-3 px-6 text-sm font-medium text-slate-300 whitespace-nowrap font-source-sans"
          >
            {item}
            <span className="w-1.5 h-1.5 rounded-full bg-brand-orange flex-shrink-0" />
          </span>
        ))}
      </div>
    </section>
  );
}

/* ─── Procurement Transformation Moment ─── */
function TransformationMoment() {
  const stats = [
    { display: '92%', numericValue: 92, label: 'of CPOs planning or assessing generative AI capabilities', source: 'Deloitte Global CPO Survey' },
    { display: '20%', numericValue: 20, label: 'higher cost savings for teams using AI-driven analytics', source: 'Gartner / McKinsey' },
    { display: '30%', numericValue: 30, label: 'faster supplier selection with AI-powered procurement', source: 'McKinsey & Company' },
    { display: '62%', numericValue: 62, label: 'of procurement leaders say AI impact will be transformational', source: 'Ardent Partners, 2025' },
  ];

  return (
    <section className="py-20 lg:py-32 bg-brand-orange" data-testid="transformation-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-5xl text-brand-navy tracking-tight leading-tight mb-6">
              The World&apos;s CPOs Are Betting on AI-Powered Procurement.{' '}
              <span className="text-brand-navy">Are You?</span>
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-brand-navy/80 text-base sm:text-lg leading-relaxed font-source-sans max-w-3xl">
              Procurement is undergoing its most significant transformation in decades. AI and
              agentic automation are reshaping the function from a back-office cost center into a
              strategic value driver. The organisations that move now will build lasting competitive
              advantage - those that wait risk falling permanently behind.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 100}>
              <AnimatedStat {...stat} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── End-to-End Source-to-Pay Coverage ─── */
function ServicesOverview() {
  const serviceItems = [
    {
      title: 'Strategy & Design',
      meta: 'Phase 01',
      description: 'Current-state assessment, future-state blueprint, and business case development before a single line of configuration is written.',
      icon: <BarChart2 className="w-5 h-5 text-brand-orange" />,
      iconBg: 'bg-blue-900/40',
      status: 'Foundation',
      tags: ['Assessment', 'Blueprint', 'BizCase'],
    },
    {
      title: 'Sourcing & Procurement',
      meta: '60–70% faster',
      description: 'AI-assisted RFP/RFQ drafting, auction management, and supplier evaluation with structured scoring models.',
      icon: <Search className="w-5 h-5 text-sky-400" />,
      iconBg: 'bg-sky-900/40',
      status: 'AI-Native',
      tags: ['AI-Assisted', 'Auctions'],
    },
    {
      title: 'Contract Management',
      meta: 'Zero leakage',
      description: 'Digital contract authoring, AI-based clause review, milestone tracking, and automated renewal alerts.',
      icon: <FileText className="w-5 h-5 text-emerald-400" />,
      iconBg: 'bg-emerald-900/40',
      status: 'Automated',
      tags: ['Authoring', 'AI Review'],
    },
    {
      title: 'Buying & Invoicing',
      meta: '3-way match',
      description: 'Guided buying with embedded policy guardrails, catalog management, and OCR-powered invoice processing.',
      icon: <CreditCard className="w-5 h-5 text-violet-400" />,
      iconBg: 'bg-violet-900/40',
      status: 'Smart',
      tags: ['Guided Buying', 'OCR'],
    },
    {
      title: 'Supplier Network',
      meta: '360° view',
      description: 'End-to-end supplier lifecycle - onboarding, performance scorecards, and AI-based risk monitoring.',
      icon: <Network className="w-5 h-5 text-amber-400" />,
      iconBg: 'bg-amber-900/40',
      status: 'Live',
      tags: ['Onboarding', 'Risk'],
    },
    {
      title: 'Insights & Optimise',
      meta: 'Post go-live AMS',
      description: 'Spend analytics, process intelligence, continuous improvement cycles, and managed services - we stay with you after go-live.',
      icon: <TrendingUp className="w-5 h-5 text-rose-400" />,
      iconBg: 'bg-rose-900/40',
      status: 'Continuous',
      tags: ['Analytics', 'AMS'],
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-navy" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-4">
              End-to-End Source-to-Pay Coverage
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-300 text-base sm:text-lg font-source-sans">
              From strategy to stabilisation, we cover every stage of the
              procurement lifecycle with deep expertise and senior-led delivery.
            </p>
          </Reveal>
        </div>

        {/* 2-col glass card grid */}
        <Reveal delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {serviceItems.map((item) => (
              <div
                key={item.title}
                className="rounded-lg p-6 flex flex-col bg-white/5 border border-white/10 backdrop-blur-sm hover:-translate-y-1 hover:shadow-xl hover:bg-white/[0.08] transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-5">
                  <div className={`w-10 h-10 rounded ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <span className="bg-white/10 text-slate-300 px-3 py-1 rounded-full font-mono text-[11px] tracking-wide">
                    {item.status}
                  </span>
                </div>
                <h3 className="font-lexend font-semibold text-white mb-1">
                  {item.title}{' '}
                  <span className="text-slate-400 font-source-sans font-normal text-sm ml-1">{item.meta}</span>
                </h3>
                <p className="font-source-sans text-slate-400 text-sm leading-relaxed mb-5 flex-grow">
                  {item.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 rounded bg-white/10 text-slate-300 font-mono text-[11px]"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Link to services page */}
        <Reveal delay={200}>
          <div className="text-center mt-8">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:gap-3 transition-all text-sm font-source-sans"
              data-testid="services-explore-link"
            >
              Explore All Services <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Team Photo Break ─── */
function TeamPhotoBreak() {
  return (
    <section className="py-10 bg-white" data-testid="team-photo-break">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-xl overflow-hidden shadow-sm border border-brand-border" style={{ borderRadius: '12px' }}>
            <img
              src="/images/team-collab.jpg"
              alt="Enterprise procurement consulting team collaboration"
              loading="lazy"
              className="w-full h-64 lg:h-80 object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Technology Stack (LogoCloud Grid) ─── */
function TechStack() {
  const items = [
    'Strategic Sourcing',
    'Buying & Invoicing',
    'Contract Management',
    'Supplier Lifecycle',
    'Cloud & BTP',
    'AI & Document Intelligence',
    'Business Networks',
    'Commerce Automation',
    'Process Intelligence / Celonis',
    'ERP Integration',
    'Spend Analytics',
    'Managed Services (AMS)',
  ];

  return (
    <section className="py-16 lg:py-24 bg-brand-orange" data-testid="tech-stack">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy tracking-tight mb-3">
              Our Procurement &amp; Supply Chain Technology Ecosystem
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-brand-navy/80 text-sm sm:text-base font-source-sans max-w-xl mx-auto">
              Deep, niche expertise across the full source-to-pay, supplier
              management, procurement automation, and AI-driven transformation
              stack - beyond generic ERP implementations.
            </p>
          </Reveal>
        </div>
        <Reveal delay={120}>
          <div className="border border-brand-navy/20 rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((item, i) => (
                <div
                  key={item}
                  className={`
                    group px-5 py-5 bg-white cursor-default
                    flex items-center justify-center text-center
                    hover:bg-brand-navy transition-colors duration-200
                    ${i % 4 !== 3 ? 'border-r border-brand-navy/10' : ''}
                    ${i < items.length - 4 ? 'border-b border-brand-navy/10' : ''}
                  `}
                >
                  <span className="font-source-sans text-sm font-medium text-brand-navy group-hover:text-white transition-colors duration-200 leading-tight">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Proven Results ─── */
function CaseStudiesTeaser() {
  const cases = [
    {
      icon: <BarChart2 className="w-4 h-4 text-brand-orange" />,
      industry: 'Amusement & Entertainment',
      geo: 'Singapore',
      duration: '8-month transformation',
      metric: '60–70%',
      metricLabel: 'faster sourcing cycles',
      highlight: 'Maverick spend significantly reduced',
      features: [
        'Strategic Sourcing & Guided Buying',
        'Procurement Automation deployed',
        'Full S2P platform go-live',
        'Maverick spend eliminated',
      ],
    },
    {
      icon: <TrendingUp className="w-4 h-4 text-sky-400" />,
      industry: 'FMCG / Entertainment / Tobacco',
      geo: 'Multi-region',
      duration: '5-month transformation',
      metric: '50–65%',
      metricLabel: 'faster procurement cycles',
      highlight: 'Improved spend visibility & control',
      features: [
        'Sourcing Optimization deployed',
        'Commerce Automation enabled',
        'Supplier Collaboration improved',
        'Multi-region rollout completed',
      ],
    },
    {
      icon: <FileText className="w-4 h-4 text-emerald-400" />,
      industry: 'Professional Services / IT',
      geo: 'India',
      duration: '4-month transformation',
      metric: '100%',
      metricLabel: 'contract digitization',
      highlight: 'Near-zero process leakage achieved',
      features: [
        'Contract Lifecycle Management',
        'Supplier Governance improved',
        'Workflow Automation deployed',
        'Near-zero process leakage',
      ],
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-navy" data-testid="case-studies-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-4">
          <div>
            <Reveal>
              <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-3">
                Proven Results
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <p className="text-slate-300 text-sm sm:text-base font-source-sans">
                Client confidentiality is paramount - all case studies are anonymised by industry.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {cases.map((c, i) => (
            <Reveal key={c.industry} delay={i * 80}>
              <div
                className="relative w-full rounded-xl border border-white/10 bg-white overflow-hidden h-full flex flex-col hover:-translate-y-2 hover:shadow-2xl transition-all duration-300"
                data-testid={`case-study-card-${i + 1}`}
              >
                {/* Card Header */}
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-slate-700 text-sm font-medium font-source-sans">
                      {c.icon}
                      <span className="truncate max-w-[160px]">{c.industry}</span>
                    </div>
                    <span className="border border-slate-200 text-slate-500 rounded-full px-2.5 py-0.5 font-mono text-[11px] flex-shrink-0">
                      {c.geo}
                    </span>
                  </div>
                  <p className="text-slate-400 font-mono text-[11px] uppercase tracking-wide mb-4">{c.duration}</p>
                  <div className="flex items-end gap-1.5 mb-2">
                    <span className="font-lexend text-5xl font-extrabold text-brand-navy tracking-tight leading-none">
                      {c.metric}
                    </span>
                    <span className="text-slate-500 pb-1 text-sm font-source-sans">{c.metricLabel}</span>
                  </div>
                  <p className="font-mono text-[11px] font-semibold text-brand-orange uppercase tracking-wide mt-2">
                    {c.highlight}
                  </p>
                </div>

                {/* Card Body */}
                <div className="flex-1 p-6 bg-slate-50 space-y-3">
                  {c.features.map((f) => (
                    <div key={f} className="flex items-start gap-3 font-source-sans text-sm text-slate-600">
                      <div className="w-4 h-4 rounded-full bg-brand-orange/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={10} className="text-brand-orange" />
                      </div>
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:gap-3 transition-all font-source-sans"
            data-testid="view-all-case-studies"
          >
            View All Case Studies <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── The iMANE Advantage ─── */
function ComparisonTable() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const usps = [
    { dimension: 'Focus', value: 'Deep domain niche - Supply Chain & Procurement only' },
    { dimension: 'Delivery Speed', value: '4–8 month rapid deployments' },
    { dimension: 'Team Model', value: 'Senior-led throughout every engagement' },
    { dimension: 'Engagement Style', value: 'Partner-to-partner collaboration' },
    { dimension: 'Value Model', value: 'Fixed outcome-based pricing' },
    { dimension: 'IP & Accelerators', value: 'Industry-specific templates & tools' },
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white" data-testid="comparison-table-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              The iMANE Advantage
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-500 text-base font-source-sans leading-relaxed">
              A dedicated team of specialists who live and breathe source-to-pay -
              delivering faster, more precise, more outcome-focused engagements
              every time.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="comparison-table">
          {usps.map((usp, i) => (
            <div
              key={usp.dimension}
              className="flex items-start gap-4 p-6 rounded-xl border border-slate-200 bg-white hover:border-brand-orange/40 hover:shadow-md transition-all duration-200"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.45s ease ${i * 70}ms, transform 0.45s ease ${i * 70}ms, box-shadow 0.2s, border-color 0.2s`,
              }}
            >
              {/* Green checkmark */}
              <div className="flex-shrink-0 mt-0.5">
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="10" fill="#22c55e" />
                  <path d="M6 10l2.5 3 5.5-5" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-mono text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                  {usp.dimension}
                </p>
                <span className="font-source-sans text-slate-800 font-medium leading-snug text-sm">
                  {usp.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Market Intelligence Stats (Bento Grid) ─── */
function MarketStats() {
  return (
    <section className="py-16 md:py-28 bg-brand-orange" data-testid="market-stats">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              The Procurement Transformation Imperative
            </h2>
            <p className="text-brand-navy text-base font-source-sans">
              The data is clear: organisations that invest in intelligent procurement
              transformation consistently outperform those that don&apos;t.
            </p>
          </div>
        </Reveal>

        {/* Bento Grid */}
        <div className="relative z-10 grid grid-cols-6 gap-3">

          {/* Card 1 — 94% AI Adoption */}
          <div className="col-span-full flex overflow-hidden lg:col-span-2 rounded-xl border border-brand-border bg-white shadow-sm" data-testid="market-stat-1">
            <div className="m-auto text-center px-6 py-8">
              <div className="relative flex h-28 w-52 items-center justify-center mb-3 mx-auto">
                <svg className="absolute inset-0 size-full text-slate-100" viewBox="0 0 254 104" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M112.891 97.7022C140.366 97.0802 171.004 94.6715 201.087 87.5116C210.43 85.2881 219.615 82.6412 228.284 78.2473C232.198 76.3179 235.905 73.9942 239.348 71.3124C241.85 69.2557 243.954 66.7571 245.555 63.9408C249.34 57.3235 248.281 50.5341 242.498 45.6109C239.033 42.7237 235.228 40.2703 231.169 38.3054C219.443 32.7209 207.141 28.4382 194.482 25.534C184.013 23.1927 173.358 21.7755 162.64 21.2989C161.376 21.3512 160.113 21.181 158.908 20.796C158.034 20.399 156.857 19.1682 156.962 18.4535C157.115 17.8927 157.381 17.3689 157.743 16.9139C158.104 16.4588 158.555 16.0821 159.067 15.8066C160.14 15.4683 161.274 15.3733 162.389 15.5286C179.805 15.3566 196.626 18.8373 212.998 24.462C220.978 27.2494 228.798 30.4747 236.423 34.1232C240.476 36.1159 244.202 38.7131 247.474 41.8258C254.342 48.2578 255.745 56.9397 251.841 65.4892C249.793 69.8582 246.736 73.6777 242.921 76.6327C236.224 82.0192 228.522 85.4602 220.502 88.2924C205.017 93.7847 188.964 96.9081 172.738 99.2109C153.442 101.949 133.993 103.478 114.506 103.79C91.1468 104.161 67.9334 102.97 45.1169 97.5831C36.0094 95.5616 27.2626 92.1655 19.1771 87.5116C13.839 84.5746 9.1557 80.5802 5.41318 75.7725C-0.54238 67.7259 -1.13794 59.1763 3.25594 50.2827C5.82447 45.3918 9.29572 41.0315 13.4863 37.4319C24.2989 27.5721 37.0438 20.9681 50.5431 15.7272C68.1451 8.8849 86.4883 5.1395 105.175 2.83669C129.045 0.0992292 153.151 0.134761 177.013 2.94256C197.672 5.23215 218.04 9.01724 237.588 16.3889C240.089 17.3418 242.498 18.5197 244.933 19.6446C246.627 20.4387 247.725 21.6695 246.997 23.615C246.455 25.1105 244.814 25.5605 242.63 24.5811C230.322 18.9961 217.233 16.1904 204.117 13.4376C188.761 10.3438 173.2 8.36665 157.558 7.52174C129.914 5.70776 102.154 8.06792 75.2124 14.5228C60.6177 17.8788 46.5758 23.2977 33.5102 30.6161C26.6595 34.3329 20.4123 39.0673 14.9818 44.658C12.9433 46.8071 11.1336 49.1622 9.58207 51.6855C4.87056 59.5336 5.61172 67.2494 11.9246 73.7608C15.2064 77.0494 18.8775 79.925 22.8564 82.3236C31.6176 87.7101 41.3848 90.5291 51.3902 92.5804C70.6068 96.5773 90.0219 97.7419 112.891 97.7022Z" fill="currentColor"/>
                </svg>
                <span className="font-jetbrains relative text-5xl font-bold text-brand-orange">94%</span>
              </div>
              <h3 className="font-lexend font-semibold text-lg text-brand-navy mb-1">AI Adoption Reached</h3>
              <p className="text-slate-500 text-xs font-source-sans max-w-[190px] mx-auto leading-relaxed">of procurement executives now use GenAI weekly - a 44-point jump in one year</p>
              <p className="text-[11px] text-slate-400 mt-2 font-source-sans">AI at Wharton, 2025</p>
            </div>
          </div>

          {/* Card 2 — 25-40% Efficiency */}
          <div className="col-span-full overflow-hidden sm:col-span-3 lg:col-span-2 rounded-xl border border-brand-border bg-white shadow-sm" data-testid="market-stat-2">
            <div className="p-6 pt-8 text-center">
              <div className="relative mx-auto flex items-center justify-center w-24 h-24 mb-5">
                <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
                  <circle cx="50" cy="50" r="38" fill="none" stroke="#f1f5f9" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="38" fill="none" stroke="#E5741A" strokeWidth="8"
                    strokeDasharray="238.76" strokeDashoffset="71.6" strokeLinecap="round"/>
                </svg>
                <span className="absolute font-jetbrains text-xl font-bold text-brand-orange">40%</span>
              </div>
              <h3 className="font-lexend font-semibold text-base text-brand-navy mb-1">Efficiency Uplift</h3>
              <p className="text-slate-500 text-xs font-source-sans leading-relaxed px-2">25-40% improvement for teams fully adopting AI orchestration over 3-5 years</p>
              <p className="text-[11px] text-slate-400 mt-2 font-source-sans">McKinsey & Company, 2025</p>
            </div>
          </div>

          {/* Card 3 — 60% Manual Reduction */}
          <div className="col-span-full overflow-hidden sm:col-span-3 lg:col-span-2 rounded-xl border border-brand-border bg-brand-navy shadow-sm" data-testid="market-stat-3">
            <div className="p-6 pb-0">
              <div className="flex items-end justify-center gap-1.5 h-16 mb-0">
                {[25, 38, 50, 62, 72, 60, 45, 30].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-sm" style={{
                    height: `${h}%`,
                    backgroundColor: i === 4 ? '#E5741A' : i > 4 ? '#E5741A44' : '#ffffff22',
                    transition: 'height 0.3s ease',
                  }}/>
                ))}
              </div>
            </div>
            <div className="p-6 pt-4 text-center">
              <h3 className="font-lexend font-semibold text-base text-white mb-1">
                <span className="font-jetbrains text-brand-orange font-bold">60%</span> Less Manual Work
              </h3>
              <p className="text-slate-400 text-xs font-source-sans leading-relaxed">reduction with unified data orchestration across S2P workflows</p>
              <p className="text-[11px] text-slate-500 mt-2 font-source-sans">Focal Point Research, 2025</p>
            </div>
          </div>

          {/* Card 4 — 67% CPO Priority */}
          <div className="col-span-full overflow-hidden lg:col-span-3 rounded-xl border border-brand-border bg-brand-navy shadow-sm" data-testid="market-stat-4">
            <div className="grid sm:grid-cols-2 p-7 gap-6">
              <div className="flex flex-col justify-between space-y-4">
                <div className="w-10 h-10 rounded-full bg-brand-orange/20 border border-brand-orange/30 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#E5741A" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
                  </svg>
                </div>
                <div>
                  <div className="font-jetbrains text-5xl font-bold text-brand-orange mb-1">67%</div>
                  <h3 className="font-lexend font-semibold text-white text-sm mb-1">of CPOs rank AI decisions as #1 priority</h3>
                  <p className="text-slate-400 text-xs font-source-sans">Deloitte Global CPO Survey, 2025</p>
                </div>
              </div>
              <div className="flex flex-col justify-center space-y-3">
                {[
                  { label: 'AI-driven decisions', pct: 67 },
                  { label: 'Cost reduction', pct: 51 },
                  { label: 'Supplier risk', pct: 44 },
                  { label: 'Compliance', pct: 38 },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-400 font-source-sans">{item.label}</span>
                      <span className="text-brand-orange font-jetbrains font-semibold">{item.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-orange rounded-full" style={{ width: `${item.pct}%` }}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 5 — $1M+ & 10-30% */}
          <div className="col-span-full overflow-hidden lg:col-span-3 rounded-xl border border-brand-border bg-white shadow-sm" data-testid="market-stat-5">
            <div className="grid sm:grid-cols-2 h-full">
              <div className="p-7 flex flex-col justify-between">
                <div className="mb-3">
                  <svg className="w-full" viewBox="0 0 200 55" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: '55px' }}>
                    <path d="M0 50 C30 48,50 40,80 28 C110 16,140 8,200 3" stroke="#E5741A" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                    <path d="M0 50 C30 48,50 40,80 28 C110 16,140 8,200 3 L200 55 L0 55Z" fill="url(#sGrad)"/>
                    <defs>
                      <linearGradient id="sGrad" x1="0" y1="0" x2="0" y2="55" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#E5741A" stopOpacity="0.15"/>
                        <stop offset="1" stopColor="#E5741A" stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div>
                  <div className="font-jetbrains text-4xl font-bold text-brand-orange mb-1">$1M+</div>
                  <h3 className="font-lexend font-semibold text-brand-navy text-sm mb-1">GenAI Investment by CPOs</h3>
                  <p className="text-slate-500 text-xs font-source-sans leading-relaxed">22% of CPOs plan $1M+ GenAI spend. 80% call AI a top priority.</p>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-source-sans">ProcureCon CPO Report, 2025</p>
                </div>
              </div>
              <div className="border-l border-brand-border p-7 flex flex-col justify-center space-y-3">
                <div>
                  <div className="font-jetbrains text-4xl font-bold text-brand-orange mb-1">10-30%</div>
                  <h3 className="font-lexend font-semibold text-brand-navy text-sm mb-1">ROI on Procurement Spend</h3>
                  <p className="text-slate-500 text-xs font-source-sans leading-relaxed">Returns through strategic sourcing and spend analysis - amplified by AI.</p>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-source-sans">ConsultingQuest / McKinsey</p>
                </div>
                <div className="flex gap-2 flex-wrap pt-1">
                  {['Sourcing', 'Analytics', 'Automation'].map((tag) => (
                    <span key={tag} className="text-[11px] px-2.5 py-0.5 rounded-full border border-brand-border text-slate-600 font-source-sans bg-brand-bg">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ─── CTA Section ─── */
function CTASection() {
  return (
    <section className="py-20 bg-brand-orange" data-testid="home-cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-5">
            Ready to make procurement your strategic advantage?
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p className="text-orange-100 text-base sm:text-lg max-w-2xl mx-auto mb-8 font-source-sans">
            Talk to a senior Supply Chain &amp; Procurement specialist at iMANE.
            Whether you are starting your S2P journey or optimising an
            existing procurement environment - we have the expertise to
            accelerate your outcomes.
          </p>
        </Reveal>
        <Reveal delay={160}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex flex-col items-center">
              <a
                href="https://wa.me/919637625311"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-white"
                data-testid="home-cta-consultation-btn"
              >
                Talk to us
              </a>
            </div>
            <div className="flex flex-col items-center">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="btn-white-outline"
                data-testid="home-cta-deck-btn"
              >
                Download Capability Deck
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Home Page ─── */
export default function Home() {
  return (
    <div data-testid="home-page">
      <Helmet>
        <title>iMANE by Mane Labs LLP | Procurement &amp; Supply Chain Transformation Consultants India</title>
        <meta name="description" content="iMANE (Mane Labs LLP) delivers boutique procurement and supply chain transformation across India and APAC. Senior-led SAP Ariba specialists. 4-8 month outcome-based deployments." />
        <meta name="keywords" content="iMANE, mane labs, mane labs LLP, procurement transformation India, supply chain consulting India, SAP Ariba consultants India, source to pay consulting, S2P consulting APAC" />
        <link rel="canonical" href="https://www.imane.in/" />
        <meta property="og:title" content="iMANE by Mane Labs LLP | Procurement &amp; Supply Chain Transformation Consultants India" />
        <meta property="og:description" content="Boutique procurement and supply chain transformation. Senior-led SAP Ariba specialists. 4-8 month deployments across India and APAC." />
        <meta property="og:url" content="https://www.imane.in/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "iMANE",
          "legalName": "Mane Labs LLP",
          "description": "Boutique procurement and supply chain transformation consulting. SAP Ariba, source-to-pay, strategic sourcing.",
          "url": "https://www.imane.in",
          "telephone": "+919637625311",
          "email": "info@imane.in",
          "address": {"@type": "PostalAddress", "streetAddress": "Kalpataru Harmony, Wakad", "addressLocality": "Pune", "addressRegion": "Maharashtra", "postalCode": "411057", "addressCountry": "IN"}
        })}</script>
      </Helmet>
      <NewHeroSection />
      <LogosSection />
      <StatsBar />
      <IndustryTicker />
      <TransformationMoment />
      <ServicesOverview />
      <TeamPhotoBreak />
      <TechStack />
      <CaseStudiesTeaser />
      <ComparisonTable />
      <MarketStats />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
}
